// podtemat1_definicja_types/LogSimpleTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

/*
  Zadania z definicji logarytmu.

  Wariant A:  oblicz wartość logarytmu  log_a b
  Wariant B:  wyznacz x z równania      log_a x = k

  Wariant B ma w podglądzie i w "Twoja odpowiedź" zapis x = …,
  natomiast w wariancie A pokazujemy wyłącznie liczbę.
*/

// ── helpers ─────────────────────────────────────────────────
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// potęga całkowita (także ujemna)
const powi = (a, k) => (k >= 0 ? Math.pow(a, k) : 1 / Math.pow(a, -k));

// zapis a^k (przydatny w krokach pośrednich)
function valueLatexForPow(a, k) {
  if (k === 0) return "1";
  if (k > 0) return `${a}^{${k}}`;
  const m = -k;
  return `\\dfrac{1}{${a}^{${m}}}`;
}

// zapis końcowy – uproszczona liczba (np. 16, 1/27)
function valueLatexSimplified(a, k) {
  if (k === 0) return "1";
  if (Number.isInteger(k)) {
    if (k > 0) {
      return String(a ** k);
    }
    const m = -k;
    return `\\dfrac{1}{${a ** m}}`;
  }
  // na wszelki wypadek – ale w naszych losowaniach k jest całkowite
  return valueLatexForPow(a, k);
}

// ── generator ──────────────────────────────────────────────

export function generateTask(options = {}) {
  // warianty po równo
  const variant = options.variant === "A" || options.variant === "B"
    ? options.variant
    : Math.random() < 0.5 ? "A" : "B";

  // podstawa 2–9
  const a = pick([2, 3, 4, 5, 6, 7, 8, 9]);

  // zakresy wykładników zależne od podstawy
  let kRange;

  if (a === 2) {
    // dla 2: do ±10
    kRange = [...Array(21).keys()].map(i => i - 10);  // -10 … 10
  } else if (a === 3) {
    // dla 3: do ±5
    kRange = [...Array(11).keys()].map(i => i - 5);   // -5 … 5
  } else {
    // dla wszystkich 4–9: do ±3
    kRange = [-3, -2, -1, 0, 1, 2, 3];
  }

  const filteredRange = options.sign === "positive"
    ? kRange.filter((value) => value > 0)
    : options.sign === "negative"
      ? kRange.filter((value) => value < 0)
      : kRange;
  const k = pick(filteredRange.length ? filteredRange : kRange);

  if (variant === "A") {
    const bLatex = valueLatexSimplified(a, k);
    return {
      type: "LogSimpleTask",
      data: { variant, a, k, bLatex }
    };
  }

  // wariant B
  const valPow = valueLatexForPow(a, k);
  const valSimplified = valueLatexSimplified(a, k);

  return {
    type: "LogSimpleTask",
    data: {
      variant,
      a,
      k,
      valPow,
      valSimplified
    }
  };
}


// ── treść zadania ──────────────────────────────────────────

export function renderTask({ data }) {
  if (data.variant === "A") {
    const { a, bLatex } = data;
    return (
      <div style={{ marginBottom: "12px" }}>
        <div>Oblicz wartość logarytmu, korzystając z definicji:</div>
        <BlockMath math={`\\log_{${a}} ${bLatex}`} />
      </div>
    );
  }

  const { a, k } = data;
  return (
    <div style={{ marginBottom: "12px" }}>
      <div>
        Skorzystaj z definicji i wyznacz <InlineMath math={"x"} />:
      </div>
      <BlockMath math={`\\log_{${a}} x = ${k}`} />
    </div>
  );
}

// ── input ──────────────────────────────────────────────────

export function renderInput(task, userAnswer, onChange, disabled) {
  const isVariantA = task?.data?.variant === "A";

  const handleChange = (ans) => {
    onChange && onChange(ans);
  };

  return (
    <div className="space-y-2">
      {/* Mały prefiks nad polem – tylko dla wariantu z x */}
      <div>
        <InlineMath math={isVariantA ? "" : "x ="} />
      </div>

      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        // dla A pusty label → podgląd to sama liczba,
        // dla B label „x” → podgląd wbudowany ma postać „x = …”
        label={isVariantA ? "" : "x"}
      />
    </div>
  );
}

// ── sprawdzanie ────────────────────────────────────────────

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const v = userAnswer.parsed;
  if (v == null) return false;

  if (data.variant === "A") {
    return Math.abs(v - data.k) < 1e-4;
  }

  const expectedX = powi(data.a, data.k);
  return Math.abs(v - expectedX) < 1e-4;
}

// ── podgląd odpowiedzi użytkownika ─────────────────────────
// Obsługujemy obie możliwe kolejności argumentów: (task, userAnswer)
// i (userAnswer, task) – na wszelki wypadek.

export function renderUserAnswer(arg1, arg2) {
  let task;
  let userAnswer;

  if (arg1 && arg1.type && arg1.data) {
    // (task, userAnswer)
    task = arg1;
    userAnswer = arg2;
  } else {
    // (userAnswer, task)
    userAnswer = arg1;
    task = arg2;
  }

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  const valLatex = formatLatexPreview(userAnswer.raw);

  if (task?.data?.variant === "B") {
    return <InlineMath math={`x = ${valLatex}`} />;
  }

  return <InlineMath math={valLatex} />;
}

// ── poprawna odpowiedź ─────────────────────────────────────

export function renderAnswer({ data }) {
  if (data.variant === "A") {
    // sama liczba, bo pytamy o wartość logarytmu
    return <InlineMath math={String(data.k)} />;
  }

  // wariant B – chcemy mieć „x = …”
  return <InlineMath math={`x = ${data.valSimplified}`} />;
}

// ── wyjaśnienia ────────────────────────────────────────────

export function renderExplanation({ data }) {
  const { variant, a, k } = data;

  if (variant === "A") {
    const bLatex = data.bLatex;
    return (
      <div className="space-y-2">
        <p>
          <strong>Krok 1.</strong> Zapisujemy definicję logarytmu:
        </p>
        <BlockMath math={"\\log_{a} b = c \\iff a^{c} = b"} />
        <p>
          <strong>Krok 2.</strong> Podstawiamy{" "}
          <InlineMath math={`a = ${a}`} /> i{" "}
          <InlineMath math={`b = ${bLatex}`} /> i szukamy wykładnika{" "}
          <InlineMath math={"k"} />, dla którego{" "}
          <InlineMath math={`${a}^{k} = ${bLatex}`} />.
        </p>
        <p>
          <strong>Wniosek:</strong>{" "}
          <InlineMath math={`\\log_{${a}} ${bLatex} = ${k}`} />.
        </p>
      </div>
    );
  }

  const valPow = data.valPow;
  const valSimplified = data.valSimplified;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1.</strong> Korzystamy z definicji logarytmu:
      </p>
      <BlockMath math={"\\log_{a} x = k \\iff a^{k} = x"} />

      <p>
        <strong>Krok 2.</strong> Podstawiamy dane:
      </p>
      <BlockMath math={`\\log_{${a}} x = ${k}`} />

      <p>
        <strong>Krok 3.</strong> Obliczamy wartość:
      </p>
      <BlockMath
        math={`x = ${a}^{${k}} = ${valPow} = ${valSimplified}`}
      />
    </div>
  );
}
